import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useStageSave } from "@/queries/recruit/use-stages.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiPipelineStage } from "@/types/recruit/stages.types.ts";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { z } from "zod";

export interface StageEditModalProps extends ModalProps {
  stage: ApiPipelineStage;
}

export function StageEditModal({ stage, ...props }: StageEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const stageSchema = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    emoji: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });

  type StageSchema = z.infer<typeof stageSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<StageSchema>({
    mode: "onSubmit",
    resolver: zodResolver(stageSchema),
    defaultValues: {
      title: stage.title,
      emoji: stage.emoji || emojiDefaults.stages.default,
    },
  });

  const stageSave = useStageSave(control);

  const onSubmit = handleSubmit((stageData) => {
    stageSave.mutate(
      {
        id: stage.id,
        payload: {
          title: stageData.title,
          emoji: stageData.emoji,
        },
      },
      {
        onSuccess: (data) => {
          if (stage.id == 0) {
            analyticsService.trackEvent(analyticEvents.pipelines.stageCreated, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.pipelines.stageEdited, {
              [analyticProperties.id]: stage.id,
            });
          }
          close(id);
        },
      }
    );
  });

  return (
    <>
      <Modal
        {...props}
        title={t("ats:stage")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button type={"submit"} size={"lg"} isLoading={stageSave.isPending} onClick={onSubmit}>
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap={"2xl"}>
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <EmojiPickerDropdown
                  onEmojiSelect={(emoji) => {
                    onChange(emoji);
                  }}
                >
                  <Box className={"relative w-41 h-41 group cursor-pointer "}>
                    <Avatar variant={"opaque"} url={undefined} emoji={value} size={"2xl"} />
                    <Box
                      className={
                        "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                      }
                    >
                      <FontAwesomeIcon
                        icon={"fa-light fa-icons"}
                        className={"text-[74px] text-light "}
                      />
                    </Box>
                  </Box>
                </EmojiPickerDropdown>
              )}
              name={"emoji"}
            />
            <Input
              required={true}
              label={t("common:title")}
              autoFocus={true}
              type="text"
              {...register("title", {
                value: stage.title,
              })}
              error={errors.title?.message}
            />
          </Stack>
        </form>
      </Modal>
    </>
  );
}
